import { Injectable } from '@angular/core';
import { HttpClient, HttpContext, HttpHeaders } from '@angular/common/http';
import { Router } from '@angular/router';
import { map, of, tap, throwError } from 'rxjs';
import { CookieService, CookieOptions } from 'ngx-cookie';
import { HttpResponse } from '@angular/common/http'
import { SessionService } from '@shared/services/auth/session.service';
import { AppService, LocalStorageService, EncryptionService } from '@shared/services/core';
import { LogService } from '@shared/services/core/log.service';
import { environment } from 'src/environments/environment';
import { EncryptionDecryptionService } from '@shared/services/core/encryption-decryption.service';
import { RSAService } from '@shared/services/core/rsa.service';

import { AccessCodeAndClientSecret, AccessCodeAndID, AccessCodeResponse, ClientUserData, ClientUserDataWithoutLogo, LogoEntryModel, MFAPrefrences, ProfessionalUserData, ProfessionalUserDataWithoutLogo } from '@shared/models/auth.interface';

import {
  APIResponse,
  AuthAPIResponse,
  CookieData,
  LoginCredentialWithClientSecret,
  RefreshedToken,
  StorageData,
  TokenAndJTI,
  UnspecifiedUserTypeData,
  UserInfo,
  UserLicenseDetail,
} from '@shared/models/auth.interface';
import { Observable } from 'rxjs';

import { BusinessError, StorageError } from '@modules/error/classes';
import { ClientList } from '@shared/models/dashboard.interface';
import { UserInfoService } from './user-info.service';
import { SKIP_LOGGING_ON_FAILURE } from '@core/tokens/meta-data.token';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private _authAPIDomain!: string;
  private _clientPortalAPIDomain!: string;
  private _uriAPIDomain!: string;
  private _licenseAPIDomain!: string;
  private _cookieOptions: CookieOptions = {};
  private _isUserLoggedIn: "y" = "y";

  constructor(
    private _appService: AppService,
    private _cookieService: CookieService,
    private _encryptionService: EncryptionService,
    private _localStorageService: LocalStorageService,
    private _userInfoService: UserInfoService,
    private _sessionService: SessionService,
    private _logService: LogService,
    private _router: Router,
    private http: HttpClient,
    private _encrDecryption:EncryptionDecryptionService,
    private rsaEncr:RSAService
  ) {
    this._authAPIDomain = this._appService.getAppConfigs()["AUTH"].BaseUrl;
    this._clientPortalAPIDomain = this._appService.getAppConfigs()["CLIENTPORTAL"].BaseUrl;
    this._licenseAPIDomain = this._appService.getAppConfigs()["LICENSE"].BaseUrl;
    this._uriAPIDomain = this._appService.getAppConfigs()["URI"].BaseUrl;
  }

  public get isUserLoggedIn() {
    return this._isUserLoggedIn;
  }
 
  /*****************************************************/
  /************ DECORATOR/HELPER FUNCTIONS *************/
  /*****************************************************/
  public saveTokenAndJTIOnSystemStorage(jti: string, token: string,ukRef:string,jtiPkr:string): void {
    this._sessionService.createOrUpdateTokenEntry(jti, token,ukRef,jtiPkr);
  }

  // public saveNewTokensOnSystemStorage(jti: string, token: string): void {
  //   this._sessionService.createOrUpdateTokenEntry(jti, token);
  // }

  public checkLicenseExpiry(licenseDetails: UserLicenseDetail): void {
    const licenseExpired: boolean = (licenseDetails.AppIsLicenseExpired === "True");
    if (licenseExpired) throw new BusinessError('USER_LICENSE_EXPIRED', "Some Description");
  }

  public getUserTypeDetailsByID(userID: string, jti: string, token: string, ukRef: string, jtiPkr: string): Observable<APIResponse<UnspecifiedUserTypeData>> {
    if (userID.startsWith('CLT')) return this.getClientDetails(userID, { token, jti, ukRef, jtiPkr });
    else if (userID.includes('PRF')) return this.getProfessionalDetails(userID, { token, jti, ukRef, jtiPkr  });
    else return throwError(() => new BusinessError('UNABLE_TO_DETERMINE_USER_TYPE', 'Some Description'));
  }

  public determineIfUserIsClient(userData: ClientUserData | ProfessionalUserData): boolean {
    return userData.hasOwnProperty('ClientID');
  }

  public performProfessionalActivationCheck(userID: string, token: string, jti: string) {
    // HERE: make sure that the list of clients does not include inactive clients.
    if (userID.includes('PRF')) {
      return this.getClientList(userID, token, jti).pipe(map(clients=>clients.Data.length>0));

      // return this.getClientList(userID, token, jti).pipe(
      //   map(clients => (clients.Data.length > 0) ? true : throwError(() => new BusinessError('USER_NOT_LINKED_WITH_CLIENTS', 'Professional not linked with any active clients.'))),
      // );
    }
    else return of(true);
  }

  public saveLogoInBase64(jti: string, logoInBase64: string, expiryTime: Date) {
    if (logoInBase64) {
      const logoEntry: LogoEntryModel = {
        logo: 'data:image/jpg;base64, ' + logoInBase64,
        expiresAt: expiryTime
      }
      this._sessionService.setLogo(jti, JSON.stringify(logoEntry));
    }
  }

  public saveAllUserDataOnSystemStorage<UnspecifiedUserTypeData>(
    userIsClient: boolean,
    userDetails: unknown,
    userLicenseDetails: UserLicenseDetail,
    token: string,
    refreshToken: string,
    jti: string
  ): void {
    const expiryTime = this._sessionService.getExpiryTime(token);;

    let dataAsString: string = '';
    let commonData: Omit<StorageData<UnspecifiedUserTypeData>, "userData"> = {
      license: userLicenseDetails,
      refreshToken: refreshToken,
      timezone: new Date().getTimezoneOffset(),
      userIsClient,
      expiresAt: expiryTime
    };

    try {
      if (userIsClient) {
        let allData: StorageData<ClientUserData> = { ...commonData, userData: userDetails as ClientUserData }
        let allDataAsString = JSON.stringify(allData);

        let userDataWithoutLogo: ClientUserDataWithoutLogo = {
          ClientID: allData.userData.ClientID,
          ClientName: allData.userData.ClientName,
          ClientEmail: allData.userData.ClientEmail,
          ClientGroupName: allData.userData.ClientGroupName,
          CLientGroupCode: allData.userData.CLientGroupCode,
          LocTypeCode: allData.userData.LocTypeCode,
          LocTypeName: allData.userData.LocTypeName,
          isAuthorizeTimesheetYN: allData.userData.isAuthorizeTimesheetYN,
          isHideComplianceYN: allData.userData.isHideComplianceYN
        }

        let dataWithoutLogo: StorageData<ClientUserDataWithoutLogo> = { ...commonData, userData: userDataWithoutLogo };
        let dataWithoutLogoAsString = JSON.stringify(dataWithoutLogo);

        const sufficientSpace = this._localStorageService.canStoreData("LOCAL", allDataAsString);
        if (sufficientSpace) {
          this._sessionService.createEncryptedStorageMapping(`data:${jti}`, dataWithoutLogoAsString);
          this.saveLogoInBase64(jti, (userDetails as ClientUserData).ClientLogo, expiryTime);
        }
      } else {
        let allData: StorageData<ProfessionalUserData> = { ...commonData, userData: userDetails as ProfessionalUserData }
        let allDataAsString = JSON.stringify(allData);

        let userDataWithoutLogo: ProfessionalUserDataWithoutLogo = {
          ProfessionalID: allData.userData.ProfessionalID,
          ProfessionalName: allData.userData.ProfessionalName,
          ProfessionalEmail: allData.userData.ProfessionalEmail,
          ProfessionalLevel: allData.userData.ProfessionalLevel,
          ProfessionalStatus: allData.userData.ProfessionalStatus,
          LevelName: allData.userData.LevelName,
        }

        let dataWithoutLogo: StorageData<ProfessionalUserDataWithoutLogo> = { ...commonData, userData: userDataWithoutLogo };
        let dataWithoutLogoAsString = JSON.stringify(dataWithoutLogo);

        const sufficientSpace = this._localStorageService.canStoreData("LOCAL", allDataAsString);
        if (sufficientSpace) {
          this._sessionService.createEncryptedStorageMapping(`data:${jti}`, dataWithoutLogoAsString);
          this.saveLogoInBase64(jti, (userDetails as ProfessionalUserData).ProfessionalLogo, expiryTime);
        }
      }
    } catch(error) {
      if (error instanceof StorageError) {
        throw new StorageError(error.title, error.description);
      } else {
        console.error(error);
      }
    }

  }

  public saveAllDataInCookie(jti: string, token: string, refreshToken: string): string {
    const cookieData: CookieData = { jti, token, refreshToken };
    const cookieDataAsString = JSON.stringify(cookieData);
    this._encryptionService.encryptData(cookieDataAsString);
    return cookieDataAsString;
  }

  setKeepMeLoggedIn(data: string) {
    let date: Date = new Date();
    date.setDate(date.getDate() + 30);
    
    this._cookieOptions.expires = date;
    this._cookieOptions.domain = window.location.hostname;
    this._cookieOptions.secure = true;
    this._cookieOptions.sameSite = 'lax';
    this._cookieOptions.storeUnencoded = true;

    if(this._cookieService.hasKey(this.encodeBase64("EntireClientPortal")) === false) {
      this._cookieService.put(this.encodeBase64("EntireClientPortal"), data, this._cookieOptions);
    } else {
      this.removeKeepMeLoggedIN();
      this._cookieService.put(this.encodeBase64("EntireClientPortal"), data, this._cookieOptions);
    }
  }

  removeKeepMeLoggedIN() {
    if(this._cookieService.hasKey(this.encodeBase64("EntireClientPortal")) === true) {
      let date: Date = new Date();
      date.setDate(date.getDate() + 30);
      
      this._cookieOptions.expires = date;
      this._cookieOptions.domain = window.location.hostname;
      this._cookieOptions.secure = true;
      this._cookieOptions.sameSite = 'lax';
      this._cookieOptions.storeUnencoded = true;
      this._cookieService.remove(this.encodeBase64("EntireClientPortal"), this._cookieOptions);
    }
  }

  checkKeepMeLoggedIn() {
    if(this._cookieService.hasKey(this.encodeBase64("EntireClientPortal")) === true) {
      let cookieData = {} as CookieData;
      let obj: string = this._cookieService.get(this.encodeBase64("EntireClientPortal")) || '';
      let encData: string = this._encryptionService.decryptData(obj);
      cookieData = JSON.parse(encData);

      this._router.navigateByUrl('/app-initialization?args4=y');
    }
  }

  resetStorageData() {
    this._localStorageService.flushStorageData();
  }

  triggerLogout(target: "GROUP" | "USER", id: string) {
    const onMobileDevice: boolean = false;
    const jti = this._sessionService.getJTIFromCurrentRoute();
    const token = this._sessionService.getToken(jti);
    
    let userType: "CLT" | "PRF" = "CLT";
    let userID: string | undefined = undefined;

    if (this._userInfoService.isUserTypeClient()) {
      userType = "CLT";
      userID = this._userInfoService.getClientInfo()?.ClientID;
    } else {
      userType = "PRF";
      userID = this._userInfoService.getProfessionalInfo()?.ProfessionalID;
    }
    
    if (target === "USER") {
      // don't need to verify ID once the connection is managed on the server side (e.g. key-value pair of userid-connectionString).
      if (userID === id) {
        this.updateLoginLogoutStatus(token, jti, userID || "", userType, "N").subscribe({
          next: (_) => {
            this._logService.appLogout("LoggedOut", { jti, user_id: userID }, onMobileDevice, token, jti);
            this.removeKeepMeLoggedIN();
            this._userInfoService.logoutUser();
            this._router.navigateByUrl(`/login?authStatus=LOGOUT`);
          },
          error: () => {}
        });
      }
    } else {
      const companyCode = this._localStorageService.getLicenseInfo("CompanyCode");
      // don't need to verify ID once the connection is managed on the server side (e.g. key-value pair of userid-connectionString).
      if (companyCode === id) {
        this.updateLoginLogoutStatus(token, jti, userID || "", userType, "N").subscribe({
          next: (_) => {
            this._logService.appLogout("LoggedOut", { jti, user_id: userID }, onMobileDevice, token, jti);
            this.removeKeepMeLoggedIN();
            this._userInfoService.logoutUser();
            this._router.navigateByUrl(`/login?authStatus=LOGOUT_FORCEFULLY`);
          },
          error: () => {}
        });
      }
    }
  }
 
  /*****************************************/
  /*************** API CALLS ***************/
  /*****************************************/
  checkAccessToken(payload: AccessCodeAndClientSecret): Observable<AuthAPIResponse<AccessCodeResponse>> {
    const authAPI = this._appService.getAppConfigs()["AUTH"].Versions.V1["ACCESS"]["AccessCode"].PATH;
    const headers = new HttpHeaders({'X-Client-Context': environment.appMetaRef});
    return this.http.post<AuthAPIResponse<AccessCodeResponse>>(`${this._authAPIDomain}${authAPI}`,
      payload,{ headers: headers,context: new HttpContext().set(SKIP_LOGGING_ON_FAILURE, true) }
    );
  }

  getTokenAndJTI(requestBody: LoginCredentialWithClientSecret): Observable<AuthAPIResponse<TokenAndJTI>> {
    const authAPI = this._appService.getAppConfigs()["AUTH"].Versions.V1["CLIENTPORTALTOKEN"]["ClientLogin"].PATH;
    const headers = new HttpHeaders({'X-Client-Context': environment.appMetaRef});
    return this.http.post<AuthAPIResponse<TokenAndJTI>>(`${this._authAPIDomain}${authAPI}`, requestBody,{ headers: headers });
  }

  getUserIDAndRefreshToken(requestBody: TokenAndJTI): Observable<APIResponse<UserInfo>> {
    const userAuthAPI = this._appService.getAppConfigs()["CLIENTPORTAL"].Versions.V1["USER"]["GetUserAuth"].PATH;
    const url = this._clientPortalAPIDomain + userAuthAPI;
    var uniqueCode = requestBody.ukRef;
    var publickKey = requestBody.jtiPkr;
    var latestTs =this._sessionService.getTimeStampValue();
    var newKey =this._encrDecryption.generateSecretKey(8);
    var header1 =this._encrDecryption.encryptData((requestBody.jti+"::"+uniqueCode),newKey);
    var header2 =this._encrDecryption.encryptData((uniqueCode+"::"+latestTs),newKey);
    var header3 =this.rsaEncr.encryptWithPublicKey(newKey, publickKey);
    const authHeaders = new HttpHeaders({
       'Authorization': `Bearer ${requestBody.token}`, 'X-Er-Type': `${requestBody.jti}`
       , 'X-PX-SIG-991C': `${header1}`, 'X-TK-PULSE-18C9': `${header2}`, 'X-RSA-FEED-09AF': `${header3}` });
    return this.http.get<APIResponse<UserInfo>>( `${url}?UserKey=${requestBody.jti}`, { headers: authHeaders })
  }

  getUserIDAndRefreshTokenJti(requestBody: TokenAndJTI): Observable<APIResponse<UserInfo>> {
    var uniqueCode = requestBody.ukRef;
    var publickKey = requestBody.jtiPkr;
    var latestTs =this._sessionService.getTimeStampValue();
    var newKey =this._encrDecryption.generateSecretKey(8);
    var header1 =this._encrDecryption.encryptData((requestBody.jti+"::"+uniqueCode),newKey);
    var header2 =this._encrDecryption.encryptData((uniqueCode+"::"+latestTs),newKey);
    var header3 =this.rsaEncr.encryptWithPublicKey(newKey, publickKey);
    const userAuthAPI = this._appService.getAppConfigs()["CLIENTPORTAL"].Versions.V1["USER"]["GetUserAuth"].PATH;
    const url = this._clientPortalAPIDomain + userAuthAPI;
    const authHeaders = new HttpHeaders({ 'Authorization': `Bearer ${requestBody.token}`, 'X-Er-Type': `${requestBody.jti}`
    , 'X-PX-SIG-991C': `${header1}`, 'X-TK-PULSE-18C9': `${header2}`, 'X-RSA-FEED-09AF': `${header3}` });

    return this.http.get<APIResponse<UserInfo>>( `${url}?UserKey=${requestBody.jti}`, { headers: authHeaders })
  }

  getRefreshedToken(currentExpiredToken: string, userRefreshToken: string): Observable<AuthAPIResponse<RefreshedToken>> {
    const refreshTokenAPI = this._appService.getAppConfigs()["AUTH"].Versions.V1["CLIENTPORTALREFRESHTOKEN"]["RefreshToken"].PATH;
    const url = this._authAPIDomain + refreshTokenAPI;
    return this.http.post<AuthAPIResponse<RefreshedToken>>(url, { token: currentExpiredToken, refreshToken: userRefreshToken });
  }

  getLicenseDetails(token: string, jti: string, ukRef: string, jtiPkr: string) {
    var uniqueCode = ukRef;
    var publickKey = jtiPkr;
    var latestTs =this._sessionService.getTimeStampValue();
    var newKey =this._encrDecryption.generateSecretKey(8);
    var header1 =this._encrDecryption.encryptData((jti+"::"+uniqueCode),newKey);
    var header2 =this._encrDecryption.encryptData((uniqueCode+"::"+latestTs),newKey);
    var header3 =this.rsaEncr.encryptWithPublicKey(newKey, publickKey);
    const userLicenseAPI = this._appService.getAppConfigs()["LICENSE"].Versions.V1["LOGGEDINUSERCOUNT"]["LoggedInUserCount"].PATH;
    const url = this._licenseAPIDomain + userLicenseAPI;
    const authHeaders = new HttpHeaders({ 'Authorization': `Bearer ${token}`, 'X-Er-Type': `${jti}`
    , 'X-PX-SIG-991C': `${header1}`, 'X-TK-PULSE-18C9': `${header2}`, 'X-RSA-FEED-09AF': `${header3}` 
     });
    return this.http.get<APIResponse<UserLicenseDetail>>(url, { headers: authHeaders })
  }

  getClientDetails(userID: string, tokenAndJTI: TokenAndJTI) {
    var uniqueCode = tokenAndJTI.ukRef;
    var publickKey = tokenAndJTI.jtiPkr;
    var latestTs =this._sessionService.getTimeStampValue();
    var newKey =this._encrDecryption.generateSecretKey(8);
    var header1 =this._encrDecryption.encryptData((tokenAndJTI.jti+"::"+uniqueCode),newKey);
    var header2 =this._encrDecryption.encryptData((uniqueCode+"::"+latestTs),newKey);
    var header3 =this.rsaEncr.encryptWithPublicKey(newKey, publickKey);
    const clientAuthAPI = this._appService.getAppConfigs()["CLIENTPORTAL"].Versions.V1["USER"]["GetClientAuth"].PATH;
    const url = this._clientPortalAPIDomain + clientAuthAPI;
    const authHeaders = new HttpHeaders({ 'Authorization': `Bearer ${tokenAndJTI.token}`, 'X-Er-Type': `${tokenAndJTI.jti}`
    , 'X-PX-SIG-991C': `${header1}`, 'X-TK-PULSE-18C9': `${header2}`, 'X-RSA-FEED-09AF': `${header3}` });
    return this.http.get<APIResponse<ClientUserData>>(`${url}?UserKey=${tokenAndJTI.jti}&ClientId=${userID}`, { headers: authHeaders })
  }

  getProfessionalDetails(userID: string, tokenAndJTI: TokenAndJTI) {
    var uniqueCode = tokenAndJTI.ukRef;
    var publickKey = tokenAndJTI.jtiPkr;
    var latestTs =this._sessionService.getTimeStampValue();
    var newKey =this._encrDecryption.generateSecretKey(8);
    var header1 =this._encrDecryption.encryptData((tokenAndJTI.jti+"::"+uniqueCode),newKey);
    var header2 =this._encrDecryption.encryptData((uniqueCode+"::"+latestTs),newKey);
    var header3 =this.rsaEncr.encryptWithPublicKey(newKey, publickKey);
    const professionalAuthAPI = this._appService.getAppConfigs()["CLIENTPORTAL"].Versions.V1["USER"]["GetProfessionalAuth"].PATH;
    const url = this._clientPortalAPIDomain + professionalAuthAPI;
    const authHeaders = new HttpHeaders({ 'Authorization': `Bearer ${tokenAndJTI.token}`, 'X-Er-Type': `${tokenAndJTI.jti}`
    , 'X-PX-SIG-991C': `${header1}`, 'X-TK-PULSE-18C9': `${header2}`, 'X-RSA-FEED-09AF': `${header3}` });
    return this.http.get<APIResponse<ProfessionalUserData>>(`${url}?UserKey=${tokenAndJTI.jti}&ProfessionalId=${userID}`, { headers: authHeaders });
  }

  recoverPassword(requestBody: AccessCodeAndID) {
    //const forgotPasswordAPI = this._appService.getAppConfigs()["CLIENTPORTAL"].Versions.V1["FORGOTPASSWORD"]["forgotPassword-v2"].PATH;
    const forgotPasswordAPI = this._appService.getAppConfigs()["CLIENTPORTAL"].Versions.V1["FORGOTPASSWORD"]["forgotPassword"].PATH;
    const url = this._clientPortalAPIDomain + forgotPasswordAPI;
    return this.http.post<APIResponse<boolean>>(url, requestBody);
  }
  recoverPasswordv2(requestBody: AccessCodeAndID) {
    const forgotPasswordAPI = this._appService.getAppConfigs()["CLIENTPORTAL"].Versions.V1["FORGOTPASSWORD"]["forgotPasswordv2"].PATH;
    const url = this._clientPortalAPIDomain + forgotPasswordAPI;
    return this.http.post<APIResponse<boolean>>(url, requestBody);
  }
  recoverUserID(requestBody: any) {
    const forgotPasswordAPI = this._appService.getAppConfigs()["CLIENTPORTAL"].Versions.V1["FORGOTPASSWORD"]["forgotuserid"].PATH;
    const url = this._clientPortalAPIDomain + forgotPasswordAPI;
    return this.http.post<APIResponse<boolean>>(url, requestBody);
  }

  getCompleteUrl(ShortCode: string) {
    const uriDecodeAPI = this._appService.getAppConfigs()["URI"].Versions.V1["URICODER"]["UriDecode"].PATH;
    const url = this._uriAPIDomain + uriDecodeAPI;

    const ApiKey=this._appService.getAppSettings().cfgPkg.cfgA;
    // HERE: create dot separated value random(apiKey,agentID).
    const AgentId=this._appService.getAppSettings().cfgPkg.cfgB;
    const Base64ApiKey=this.encodeBase64(ApiKey);
    const Base64AgentId=this.encodeBase64(AgentId);

    const stringSequence = (Math.floor(Math.random() * 10) > 5) ? `${Base64AgentId}.${Base64ApiKey}` : `${Base64ApiKey}.${Base64AgentId}`;

    const authHeaders = new HttpHeaders({ 'ApiKey': `${stringSequence}` });
    return this.http.post<APIResponse<string>>(url, { ShortCode:ShortCode}, { headers: authHeaders });
  }

  public getClientList(clientId: string, token: string, jti: string) {
    const userAuthAPIEndpoint = this._appService.getAppConfigs()["CLIENTPORTAL"].Versions["V1"]["DASHBOARD"]["GetClientList"].PATH;
    const url = this._clientPortalAPIDomain + userAuthAPIEndpoint;
    var uniqueCode = this._sessionService.getUkRef(jti);
    var publickKey = this._sessionService.getJtiPkr(jti);
    var latestTs =this._sessionService.getTimeStampValue();
    var newKey =this._encrDecryption.generateSecretKey(8);
    var header1 =this._encrDecryption.encryptData((jti+"::"+uniqueCode),newKey);
    var header2 =this._encrDecryption.encryptData((uniqueCode+"::"+latestTs),newKey);
    var header3 =this.rsaEncr.encryptWithPublicKey(newKey, publickKey);
    let userTimezoneOffset = new Date().getTimezoneOffset();
    const authHeaders = new HttpHeaders({ 'Authorization': `Bearer ${token}`, 'X-Er-Type': `${jti}`, 
    'X-PX-SIG-991C': `${header1}`, 'X-TK-PULSE-18C9': `${header2}`, 'X-RSA-FEED-09AF': `${header3}` });
    //const authHeaders = new HttpHeaders({ 'Authorization': `Bearer ${token}`, 'X-Er-Type': `${jti}`
    // });
    return this.http.get<APIResponse<ClientList[]>>(`${url}?ProfessionalId=${clientId}`, { headers: authHeaders });
  }

  getOldPlientPortalLink() {
    const oldClientportalLinkAPIEndpoint =
     this._appService.getAppConfigs()['CLIENTPORTAL'].Versions['V1']['USER']['GetOldPlientPortalLink'].PATH;
    const url = this._clientPortalAPIDomain + oldClientportalLinkAPIEndpoint;
    return this.http.get<APIResponse<string>>(`${url}`);
  }

  updateLoginLogoutStatus(token: string, jti: string, userID:string, userType:"CLT"|"PRF", status:"Y"|"N") {
    const loginLogoutStatusAPIEndpoint = this._appService.getAppConfigs()['CLIENTPORTAL'].Versions['V1']['LOGINLOGOUTSTATUS']['loginLogoutStatus'].PATH;
    const url = this._clientPortalAPIDomain + loginLogoutStatusAPIEndpoint;
    var uniqueCode = this._sessionService.getUkRef(jti);
    var publickKey = this._sessionService.getJtiPkr(jti);
    var latestTs =this._sessionService.getTimeStampValue();
    var newKey =this._encrDecryption.generateSecretKey(8);
    var header1 =this._encrDecryption.encryptData((jti+"::"+uniqueCode),newKey);
    var header2 =this._encrDecryption.encryptData((uniqueCode+"::"+latestTs),newKey);
    var header3 =this.rsaEncr.encryptWithPublicKey(newKey, publickKey);
    let userTimezoneOffset = new Date().getTimezoneOffset();
    const authHeaders = new HttpHeaders({ 'Authorization': `Bearer ${token}`, 'X-Er-Type': `${jti}`, 
    'X-PX-SIG-991C': `${header1}`, 'X-TK-PULSE-18C9': `${header2}`, 'X-RSA-FEED-09AF': `${header3}` });
    return this.http.post<APIResponse<string>>(url, { UserLoginStatus:status, UserId:userID, Portal:userType,userTimezoneOffset:userTimezoneOffset}, { headers: authHeaders });
  }

  encodeBase64 = (data: any) => {
    return Buffer.from(data).toString('base64');
  }

  private decodeBase64 = (data: any) => {
      return Buffer.from(data, 'base64').toString('ascii');
  }

  getMFAWithPrefences(requestBody: LoginCredentialWithClientSecret): Observable<HttpResponse<AuthAPIResponse<MFAPrefrences>>>  {
    const authAPI = this._appService.getAppConfigs()["AUTH"].Versions.V1["CLIENTPORTALTOKEN"]["ClientLogin"].PATH;
    const headers = new HttpHeaders({'X-Client-Context': environment.appMetaRef});
    return this.http.post<AuthAPIResponse<MFAPrefrences>>(`${this._authAPIDomain}${authAPI}`, requestBody,
    {
      headers: headers,
      observe: 'response'
    }
  );
  }
}
